import React from "react";

export interface ReadTimeProps {
  /**
   * Article body text. When provided, the read time is computed from it
   * (200 wpm). Takes precedence over `amount`.
   */
  text?: string;
  /**
   * Pre-formatted read-time string (e.g. `"5 min read"`, `"Video • 2.18 min"`).
   * Used as a fallback when `text` is not supplied.
   */
  amount?: string;
  /** Visual style — `"badge"` renders a pill, `"text"` renders muted inline text. */
  variant: 'text' | 'badge';
}

/**
 * Renders an estimated read-time label for an article card or detail page.
 *
 * Computes minutes from `text` using the Nielsen Norman Group rule of thumb of
 * 200 words per minute (https://www.nngroup.com/articles/reading-time/),
 * rounded up. If `text` is not supplied it falls back to displaying `amount`
 * verbatim. Returns `null` when neither is supplied.
 */
export default function ReadTime({ text, amount, variant }: ReadTimeProps) {
  const className = variant === 'badge' ? 'badge bg-gray-200 text-gray-700 mb-0' : 'opacity-75 mb-0';
  if (text && typeof text === 'string') {
    // Estimates reading time based on the average reading speed of 200 words per minute.
    // https://www.nngroup.com/articles/reading-time/
    const words = text.split(' ').length;
    const minutes = Math.ceil(words / 200);

    return <span className={className}>{minutes} min read</span>;
  }

  return amount ? <span className={className}>{amount}</span> : null;
}
